import Phaser from "phaser";
import BaseInteriorScene from "../BaseInteriorScene";
import arenaSource from "./voidArena-source.json";
import { getVoidCityNpcDialogue } from "../../../@shared/utils/npcDialogs";
import voidArenaBg from "../GameMap/InfiNFT Maps/voidArena.png";
import receptionistNpcPng from "../../../@shared/assets/GameMaps/tilesets/interior/receptionist.png";
import toshiSpritePng from "../../../@shared/assets/avatars/toshi-small-sprite.png";
import { getAvatarFrameForDirection, getAvatarSpriteConfig } from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import { useStoryStore } from "../../../@shared/store/useStoryStore";

const RECEPTIONIST_NPC_KEY = "arenaReceptionistNpcPortrait";
const TOSHI_NPC_KEY = "arenaStoryToshiNpc";
const RECEPTIONIST_NPC_SCALE = 0.05;
const RECEPTIONIST_MASK_OFFSET_Y = 2;
const RECEPTIONIST_FEET_OFFSET_Y = 28;
const TOSHI_MISSION_ID = "act-1-mission-5";
const TOSHI_CATALOG_CHARACTER_ID = "QW8eUlAKTTcLwX7nyKmY";
const TOSHI_PERSISTENT_MISSION_IDS = new Set([
  "act-1-mission-6",
  "act-1-mission-7",
  "act-1-mission-8",
]);
const TOSHI_SCENE_CUE_ID = "toshi-enter";
const TOSHI_ANIMATION_PREFIX = "story-toshi";
const TOSHI_ENTRY_POSITION = Object.freeze({ x: 323, y: 456 });
const TOSHI_PLAYER_SIDE_OFFSET = Object.freeze({ x: 46, y: 18 });
const TOSHI_INTERACTION_SIZE = Object.freeze({ width: 54, height: 58 });
const TOSHI_IDLE_DIRECTION = 270;

export default class ArenaScene extends BaseInteriorScene {
  constructor() {
    super(
      "ArenaScene",
      BaseInteriorScene.extractCollisionDataFromMapSource(
        arenaSource,
        "TestCollisions",
      ),
      voidArenaBg,
      { exit: { to: "VoidCity2Scene", spawnAt: { x: 718, y: 234 } } },
      getVoidCityNpcDialogue,
    );
  }

  preload() {
    super.preload();
    this.load.image(RECEPTIONIST_NPC_KEY, receptionistNpcPng);
    this.load.spritesheet(TOSHI_NPC_KEY, toshiSpritePng, {
      frameWidth: 128,
      frameHeight: 128,
    });
  }

  create(data) {
    super.create(data);

    this.createAmbientInteriorNpc({
      textureKey: "interiorAmbientNpcB",
      x: 184,
      y: 352,
      facing: "right",
    });
    this.createAmbientInteriorNpc({
      textureKey: "interiorAmbientNpcC",
      x: 522,
      y: 352,
      facing: "left",
    });

    const collisionLayer = arenaSource.layers.find(
      (layer) => layer.type === "objectgroup" && layer.name === "TestCollisions",
    );
    const receptionistAnchor = collisionLayer?.objects?.find(
      (object) => object.name === "receptionist",
    );
    const exitAnchor = collisionLayer?.objects?.find(
      (object) => object.name === "exit",
    );

    if (!receptionistAnchor) return;

    const anchorX = receptionistAnchor.x + receptionistAnchor.width / 2;
    const occlusionY =
      receptionistAnchor.y + receptionistAnchor.height + RECEPTIONIST_MASK_OFFSET_Y;
    const npcY =
      receptionistAnchor.y + receptionistAnchor.height + RECEPTIONIST_FEET_OFFSET_Y;

    const receptionist = this.add
      .image(anchorX, npcY, RECEPTIONIST_NPC_KEY)
      .setOrigin(0.5, 1)
      .setScale(RECEPTIONIST_NPC_SCALE)
      .setDepth(70);

    const maskRect = this.add
      .rectangle(0, 0, this.cameras.main.width * 2, occlusionY, 0xffffff)
      .setOrigin(0, 0)
      .setVisible(false)
      .setScrollFactor(1)
      .setDepth(69);
    receptionist.setMask(maskRect.createGeometryMask());

    this.storyToshi = null;
    this.storyToshiSensor = null;
    this.storyToshiWalking = false;

    createWalkAnimations(
      this,
      TOSHI_NPC_KEY,
      "toshi",
      TOSHI_ANIMATION_PREFIX,
    );

    const getStoryToshiTargetPosition = () => ({
      x: (this.player?.x ?? anchorX) + TOSHI_PLAYER_SIDE_OFFSET.x,
      y: (this.player?.y ?? npcY + 40) + TOSHI_PLAYER_SIDE_OFFSET.y,
    });

    const getStoryToshiEntryPosition = () =>
      exitAnchor
        ? {
            x: exitAnchor.x + exitAnchor.width / 2,
            y: exitAnchor.y + exitAnchor.height,
          }
        : TOSHI_ENTRY_POSITION;

    const buildStoryToshi = (position = getStoryToshiTargetPosition()) => {
      if (this.storyToshi) return this.storyToshi;

      const spriteConfig = getAvatarSpriteConfig("toshi");
      const scale = (spriteConfig.worldScale || 1) * 0.8;

      this.storyToshi = this.add
        .sprite(position.x, position.y, TOSHI_NPC_KEY, 0)
        .setOrigin(0.5, 1)
        .setScale(scale)
        .setDepth(position.y + 24);

      this.storyToshiSensor = this.matter.add.rectangle(
        position.x,
        position.y - TOSHI_INTERACTION_SIZE.height / 2,
        TOSHI_INTERACTION_SIZE.width,
        TOSHI_INTERACTION_SIZE.height,
        {
          isStatic: true,
          isSensor: true,
          label: "npc_toshi",
        },
      );

      this.storyToshi.setFrame(
        getAvatarFrameForDirection("toshi", TOSHI_IDLE_DIRECTION, 0),
      );

      return this.storyToshi;
    };

    const syncStoryToshiDepth = () => {
      if (!this.storyToshi) return;
      this.storyToshi.setDepth(this.storyToshi.y + 24);
      if (this.storyToshiSensor) {
        Phaser.Physics.Matter.Matter.Body.setPosition(this.storyToshiSensor, {
          x: this.storyToshi.x,
          y: this.storyToshi.y - TOSHI_INTERACTION_SIZE.height / 2,
        });
      }
    };

    const showPersistentStoryToshi = () => {
      const launchTarget = useStoryStore.getState().currentLaunchTarget;
      const storyState = useStoryStore.getState().storyState || {};
      const teammateRoles = launchTarget?.battle?.storySide?.teammateRoles || [];
      const hasToshiBattleRole = teammateRoles.some(
        (role) =>
          String(role?.alias || "").toLowerCase() === "toshi" ||
          String(role?.characterId || "") === TOSHI_CATALOG_CHARACTER_ID,
      );
      if (
        this.userData?.activeGameMode !== "story" ||
        storyState?.activeStoryAvatarKey === "toshi" ||
        (!TOSHI_PERSISTENT_MISSION_IDS.has(launchTarget?.missionId) &&
          !hasToshiBattleRole)
      ) {
        return;
      }

      buildStoryToshi(getStoryToshiTargetPosition());
      syncStoryToshiDepth();
    };

    const handleStorySceneCue = (event) => {
      const missionId = event?.detail?.missionId;
      const cueId = event?.detail?.cue?.id;

      if (
        this.userData?.activeGameMode !== "story" ||
        missionId !== TOSHI_MISSION_ID ||
        cueId !== TOSHI_SCENE_CUE_ID ||
        this.storyToshiWalking
      ) {
        return;
      }

      const entryPosition = getStoryToshiEntryPosition();
      const targetPosition = getStoryToshiTargetPosition();
      const approachPosition = {
        x: entryPosition.x,
        y: targetPosition.y,
      };

      const toshi = buildStoryToshi(entryPosition);
      toshi.setPosition(entryPosition.x, entryPosition.y);
      syncStoryToshiDepth();

      this.storyToshiWalking = true;
      toshi.play(`${TOSHI_ANIMATION_PREFIX}-up`, true);

      this.tweens.add({
        targets: toshi,
        x: approachPosition.x,
        y: approachPosition.y,
        duration: 1250,
        ease: "Sine.easeOut",
        onUpdate: () => {
          syncStoryToshiDepth();
        },
        onComplete: () => {
          const horizontalAnimation =
            targetPosition.x >= approachPosition.x
              ? `${TOSHI_ANIMATION_PREFIX}-right`
              : `${TOSHI_ANIMATION_PREFIX}-left`;

          toshi.play(horizontalAnimation, true);

          this.tweens.add({
            targets: toshi,
            x: targetPosition.x,
            y: targetPosition.y,
            duration: 700,
            ease: "Sine.easeInOut",
            onUpdate: () => {
              syncStoryToshiDepth();
            },
            onComplete: () => {
              this.storyToshiWalking = false;
              toshi.anims.stop();
              toshi.setFrame(
                getAvatarFrameForDirection("toshi", TOSHI_IDLE_DIRECTION, 0),
              );
              syncStoryToshiDepth();
            },
          });
        },
      });
    };

    showPersistentStoryToshi();
    window.addEventListener("story-scene-cue", handleStorySceneCue);

    this.events.once("shutdown", () => {
      window.removeEventListener("story-scene-cue", handleStorySceneCue);
      if (this.storyToshiSensor) {
        if (this.matter?.world?.remove) {
          try {
            this.matter.world.remove(this.storyToshiSensor);
          } catch {}
        }
        this.storyToshiSensor = null;
      }
      this.storyToshi?.destroy();
      this.storyToshi = null;
      receptionist.destroy();
      maskRect.destroy();
    });
  }
}
